import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";

import { createAutoDeleteConfig } from "../../api/create";
import { deleteAutoDeleteConfig } from "../../api/delete";
import { getAutoDeleteConfigs, getLocationsList } from "../../api/list";

import Table from "../../components/table/table";
import CommonHeader from "../../components/common-header";
import { ConfirmPopup } from "../../components/model-popup/confirmation-popup";
import { CustomButton } from "../../components/buttons/button";
import { ToggleButton } from "../../components/input-fields/toggle-button";
import { debounce } from "../../utils/redirections";
import CreateRuleModal from "./CreateRuleModal";
import { formatRuleLocations } from "./locationUtils";

import DeleteIcon from "../../assets/icons/trash.png";

import { ChildProps } from "../../utils/types/interFace";

const ERROR_TYPES = [
  { label: "No Header", value: "No header" },
  { label: "Duplicate Patient", value: "Duplicate Patient" },
  { label: "No Location", value: "No Location" },
  { label: "Information Missing", value: "Information Missing" },
];

interface fetchConfigsParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  sortDir?: string;
}
interface fetchConfigsApiParam {
  page: number;
  limit: number;
  search_value: string;
  sort_by: string;
  sort_dir: string;
}

const AutoDelete = ({ filteredAccess = [] }: Partial<ChildProps>) => {
  const userAccess = filteredAccess?.length ? filteredAccess[0] : {};

  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [locationList, setLocationList] = useState<any[]>([]);
  const [sort, setSort] = useState<string>("created_at");
  const [sortDir, setSortDir] = useState<string>("desc");

  const handleSearch = useCallback(debounce(setSearch, 500), []);
  const limit = 15;

  const fetchConfigs = (param: fetchConfigsParams) => {
    const apiParam: fetchConfigsApiParam = {
      page: page,
      limit: limit,
      search_value: search,
      sort_by: sort,
      sort_dir: sortDir
    }
    if (param.page) {
      apiParam.page = param.page
      setPage(param.page)
    } else if (param.sort && param.sortDir) {
      apiParam.sort_by = param.sort
      apiParam.sort_dir = param.sortDir
      apiParam.page = 1
      setSort(param.sort)
      setSortDir(param.sortDir)
      setPage(1)
    } else if (param.search) {
      apiParam.search_value = param.search
      apiParam.page = 1
      setPage(1)
    }
    if (apiParam.page === 1) {
      setLoading(true);
    }
    getAutoDeleteConfigs(apiParam)
      .then((res: any) => {
        if (apiParam.page === 1) {
          setConfigs(res?.result?.data || []);
        } else {
          setConfigs([...configs, ...(res?.result?.data || [])])
        }
        setTotalPages(res?.result?.total_pages || 0);
        setTotalCount(res?.result?.total_count || 0);
      })
      .catch(() => toast.error("Failed to load auto-delete rules."))
      .finally(() => setLoading(false));
  };

  const fetchLocations = useCallback(async () => {
    try {
      const result = await getLocationsList();
      if (result?.result) {
        setLocationList([
        { label: "All Locations", value: "all_location", isDisabled: true },
        ...result.result.map((item: any) => ({
          label: item,
          value: item,
        })),
      ]);
      } else {
        setLocationList([]);
      }
    } catch (error) {
      console.error("Failed to load locations (getLocationsList):", error);
      setLocationList([]);
    }
  }, []);

  useEffect(() => {
    fetchConfigs({ search: search })
  }, [search]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  /** Re-fetch locations (getLocationsList) when opening Create Rule. */
  useEffect(() => {
    if (showModal) {
      fetchLocations();
    }
  }, [showModal, fetchLocations]);

  useEffect(() => {
    handleSearch(showSearch);
  }, [showSearch]);

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      error_type: "",
      location: ['all_location'] as string[],
      retention_days: 30,
      is_active: true,
    },
    validationSchema: yup.object({
      error_type: yup.string(),
      location: yup.array().of(yup.string()),
      retention_days: yup
        .number()
        .min(1, "Retention days must be at least 1")
        .required("Retention days is required"),
    }).test("at-least-one", "Provide at least Error Type or Location", function (values: any) {
      const locationList = Array.isArray(values.location) ? values.location.filter((item: string) => item !== 'all_location') : this.parent;
      const hasLocation = Array.isArray(locationList) && locationList.length > 0;
      if (!values.error_type && !hasLocation) {
        return this.createError({ path: "error_type", message: "Provide at least Error Type or Location" });
      }
      return true;
    }),
    onSubmit: async (values: any) => {
      setButtonLoader(true);
      try {
        const response = await createAutoDeleteConfig({
          ...values,
          error_type: values.error_type || null,
          location:
            Array.isArray(values.location) && values.location.length > 0
              ? (values.location.includes("all_location") ? [] : values.location)
              : null,
        });
        if (response?.success) {
          toast.success("Auto-delete rule created.");
          formik.resetForm();
          setShowModal(false);
          fetchConfigs({ page: 1 })
        } else {
          toast.error(response?.detail || response?.message || "Failed to create rule.");
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.detail || "Failed to create rule.");
      } finally {
        setButtonLoader(false);
      }
    },
  });

  const handleDelete = async () => {
    setButtonLoader(true);
    try {
      await deleteAutoDeleteConfig(deleteTarget?.id);
      toast.success("Rule deleted.");
      setShowDeleteModel(false);
      setDeleteTarget(null);
      fetchConfigs({ page: 1 })
    } catch {
      toast.error("Failed to delete rule.");
    } finally {
      setButtonLoader(false);
    }
  };

  return (
    <div className="w-full p-[10px]">
      <div className="p-[16px] bg-white rounded-[12px]">
        <CommonHeader
          search
          addNew={userAccess?.write_access}
          heading="Auto Delete"
          description="Configure auto-delete rules for error notes."
          totalCount={totalCount}
          countForText="Rules"
          handleSearch={handleSearch}
          setShowSearch={setShowSearch}
          showSearch={showSearch}
          onclickAddButton={() => {
            formik.resetForm();
            setShowModal(true);
          }}
        />

        <div className="w-full min-w-0 overflow-x-auto pt-2">
          <Table
            tableScroll={"100%"}
            loading={loading}
            hasNextPage={page < totalPages}
            data={configs}
            NextPageData={() => fetchConfigs({ page: page + 1 })}
            columns={[
              "Error Type",
              "Location",
              "Retention Days",
              ...(userAccess?.delete_access ? ["Action"] : []),
            ]}
            sortTitleKeys={{
              "Error Type": "error_type",
              "Retention Days": "retention_days",
            }}
            columnHeaderAlignments={["left", "left", "center", ...(userAccess?.delete_access ? ["center"] : [])]}
            columnClassNames={["w-[20%]", "w-[50%] ", "w-[15%]", "w-[15%]"]}
            sort={true}
            handleSortfunc={(key: any, value: any) => {
              fetchConfigs({ sort: key, sortDir: value })
            }}
            scopedSlots={{
              "Error Type": ({ row }: any) => (
                <td className="align-middle text-left max-w-0 w-[20%]">
                  {row?.error_type ? <div className="truncate">{row?.error_type || "-"}</div> : <span className="text-gray-400">All Error Types</span>}
                </td>
              ),
              Location: ({ row }: any) => {
                const locationText = formatRuleLocations(row?.location);
                return (
                  <td className="align-middle text-left max-w-0 w-[50%]" title={locationText}>
                    {locationText !== '-' ? <div className="truncate">{locationText}</div> : <span className="text-gray-400">All Locations</span>}
                  </td>
                );
              },
              "Retention Days": ({ row }: any) => (
                <td className="align-middle text-left whitespace-nowrap">{row?.retention_days}</td>
              ),
              Status: ({ row }: any) => (
                <td className="align-middle text-center">
                  <div className="flex justify-center items-center">
                    <ToggleButton
                      value={row?.is_active}
                      disabled
                      toggleStyle="w-[36px] h-[20px] after:h-[16px] after:w-[16px] after:top-[2px]"
                    />
                  </div>
                </td>
              ),
              Action: ({ row }: any) => (
                <td className="text-center align-middle" onClick={(e: any) => e.stopPropagation()}>
                  <div className="flex gap-[16px] items-center justify-center pr-[4px]">
                    {userAccess?.delete_access && (
                      <img
                        src={DeleteIcon}
                        alt="Delete"
                        className="cursor-pointer w-[18px] h-[18px]"
                        onClick={() => {
                          setDeleteTarget(row);
                          setShowDeleteModel(true);
                        }}
                      />
                    )}
                  </div>
                </td>
              ),
            }}
          />
        </div>
      </div>

      <CreateRuleModal
        open={showModal}
        onClose={() => { setShowModal(false); formik.resetForm(); }}
        formik={formik}
        locationOptions={locationList}
        errorTypeOptions={ERROR_TYPES}
        buttonLoader={buttonLoader}
      />

      {/* Delete Confirm */}
      <ConfirmPopup showModal={showDeleteModel} icon>
        <div className="text-[16px] font-semibold text-center text-[#061018]">
          Delete Rule?
        </div>
        <div className="text-[12px] font-normal text-center text-[#061018] mt-[8px] mb-[16px]">
          You are about to delete the auto-delete rule for{" "}
          <span className="font-semibold">
            {deleteTarget?.error_type || "Any error type"}
          </span>
          {" / "}
          <span className="font-semibold">
            {formatRuleLocations(deleteTarget?.location,100)}
          </span>
          {" "}
          (retain{" "}
          <span className="font-semibold">{deleteTarget?.retention_days}</span>{" "}
          days). This action cannot be undone.
        </div>
        <div className="flex justify-center gap-[16px] items-center w-full">
          <CustomButton
            buttonName="No, Keep it"
            primary={false}
            buttonStyle="w-full text-[#1C252D] text-[14px] p-[12px] font-semibold leading-[17px]"
            disable={buttonLoader}
            onClick={() => { setShowDeleteModel(false); setDeleteTarget(null); }}
          />
          <CustomButton
            buttonName="Yes, Delete!"
            buttonStyle="w-full text-white bg-[#D41E1E] hover:bg-[#B51C1C] text-[14px] p-[12px] font-semibold leading-[17px]"
            loader={buttonLoader}
            disable={buttonLoader}
            loaderStyle="w-full bg-[#D41E1E]/80 py-[13px]"
            onClick={handleDelete}
          />
        </div>
      </ConfirmPopup>
    </div>
  );
};

export default AutoDelete;
